:root {
  color-scheme: light dark;
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    "Helvetica Neue",
    Arial,
    sans-serif;
  line-height: 1.5;
  font-size: 16px;
  /* Light mode colors */
  --color-bg-primary: #eeeeee;
  --color-bg-secondary: #bcbcbc;
  --color-bg-accent: #3b82f6;
  --color-text-primary: #1f2937;
  --color-text-secondary: #575b64;
  --color-text-accent: #2563eb;
  --color-text-inverse: #ffffff;
  --color-text-dark: #252525;
  --color-bg-feature-area: rgba(184, 184, 184, 0.5);
  /* State colors */
  --color-disabled: #8a94a5;
  --color-danger: #f04444;
  --color-success: #28b55c;
  --color-warning: #eab450;
  --color-info: #4baad6;
  /* Borders & shadows */
  --border: 1px solid var(--color-text-primary);
  --box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  --border-radius: 8px;
  /* Spacing */
  --padding-xs: 0.15rem;
  --padding-s: 0.25rem;
  --padding: 0.5rem;
  --padding-l: 0.75rem;
  --padding-xl: 1rem;
  --padding-xxl: 2rem;
  --margin-s: 0.25rem;
  --margin: 0.5rem;
  --margin-l: 0.75rem;
  --margin-xl: 1rem;
  --margin-xxl: 2rem;
  /* Typography */
  --font-size-xs: 0.5rem;
  --font-size-s: 0.75rem;
  --font-size-ms: 0.875rem;
  --font-size: 1rem;
  --font-size-ml: 1.125rem;
  --font-size-l: 1.25rem;
  --font-size-xl: 1.5rem;
  --font-size-xxl: 2rem;
  --font-weight-light: 300;
  --font-weight-normal: 400;
  --font-weight-medium: 500;
  --font-weight-bold: 700;
  /* Responsive breakpoints */
  --breakpoint-mobile: 768px;
  --breakpoint-tablet: 1024px;
  /* Touch targets */
  --touch-target-min: 44px;
}
:root[data-theme="dark"] {
  --color-bg-primary: #1f2937;
  --color-bg-secondary: #374151;
  --color-bg-accent: #3b82f6;
  --color-text-primary: #f9fafb;
  --color-text-secondary: #d1d5db;
  --color-text-accent: #60a5fa;
  --color-text-inverse: #ffffff;
  --color-text-dark: #f0f0f0;
  --color-bg-feature-area: rgba(110, 110, 110, 0.5);
  --border: 1px solid var(--color-bg-secondary);
}
@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
    --color-bg-primary: #1f2937;
    --color-bg-secondary: #374151;
    --color-bg-accent: #3b82f6;
    --color-text-primary: #f9fafb;
    --color-text-secondary: #d1d5db;
    --color-text-accent: #60a5fa;
    --color-text-inverse: #ffffff;
    --color-text-dark: #f0f0f0;
    --color-bg-feature-area: rgba(110, 110, 110, 0.5);
    --border: 1px solid var(--color-bg-secondary);
  }
}

* {
  box-sizing: border-box;
}
:host {
  display: block;
}
html {
  font-size: var(--font-size);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
body {
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    "Helvetica Neue",
    Arial,
    sans-serif;
  color: var(--color-text-primary);
  background: var(--color-bg-primary);
  padding: 0;
  margin: 0;
}
#app {
  min-height: 100vh;
}
a {
  color: inherit;
  text-decoration: none;
}
button {
  font-family: inherit;
  cursor: pointer;
}
input,
textarea,
select {
  font-family: inherit;
  background-color: var(--color-bg-secondary);
}
img {
  max-width: 100%;
  height: auto;
}
label {
  display: block;
  padding: var(--padding-s) 0;
  font-size: var(--font-size);
  font-weight: var(--font-weight-light);
}
input,
select,
textarea {
  width: 100%;
  padding: var(--padding-s);
  border: var(--border);
  border-radius: var(--border-radius);
  font-size: var(--font-size);
  box-sizing: border-box;
}
input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: var(--color-bg-accent);
  box-shadow: var(--box-shadow);
}
input:disabled {
  background: var(--color-bg-secondary);
  color: var(--color-text-secondary);
}
textarea {
  min-height: 100px;
  resize: vertical;
}
.btn {
  padding: var(--padding) var(--padding-xl);
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size);
  cursor: pointer;
}
.btn .icon {
  vertical-align: text-top;
  margin-right: var(--margin);
  width: 16px;
  height: 16px;
}
.btn-primary {
  background: var(--color-bg-accent);
  color: var(--color-text-primary);
}
.btn-primary:hover {
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
}
.btn-primary:disabled {
  background: var(--color-disabled);
  cursor: not-allowed;
}
.btn-secondary {
  background: var(--color-bg-primary);
  border: var(--border);
  color: var(--color-text-primary);
}
.btn-secondary:hover {
  background: color-mix(in oklab, var(--color-bg-secondary), black 10%);
}
.btn-danger {
  background: var(--color-danger);
  color: var(--color-text-inverse);
}
.btn-success {
  background: var(--color-success);
  color: var(--color-text-inverse);
}
.btn-warn {
  background: var(--color-warning);
  color: var(--color-text-primary);
}
.btn-info {
  background: var(--color-info);
  color: var(--color-text-inverse);
}
.btn-lg {
  display: inline-block;
  padding: var(--padding-l) calc(var(--padding-xl) * 2);
  font-size: var(--font-size-l);
}
.btn-md {
  display: inline-block;
  padding: var(--padding) var(--padding-xl);
  font-size: var(--font-size);
}
.section {
  padding-bottom: var(--margin-xl);
  border-bottom: var(--border);
  margin-bottom: var(--margin-xl);
}
.section-title {
  font-size: var(--font-size-l);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--margin);
  color: var(--color-text-primary);
}
.text-muted {
  color: var(--color-text-secondary);
}
.text-danger {
  color: var(--color-danger);
}
.text-warn {
  color: var(--color-warning);
}
.text-success {
  color: var(--color-success);
}
.loading {
  text-align: center;
  padding: var(--padding-xl);
  color: var(--color-disabled);
}
.empty {
  text-align: center;
  padding: var(--padding-xl);
  color: var(--color-disabled);
}
.error {
  background: var(--color-bg-secondary);
  border: var(--border);
  color: var(--color-danger);
  padding: var(--padding);
  border-radius: var(--border-radius);
  margin-bottom: var(--margin);
  font-size: var(--font-size);
}
.toggle {
  width: 40px;
  height: 24px;
  background: var(--color-bg-secondary);
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  border: none;
}
.toggle.active {
  background: var(--color-bg-accent);
}
.toggle::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: var(--color-bg-primary);
  border-radius: 50%;
  transition: transform 0.2s;
}
.toggle.active::after {
  transform: translateX(16px);
}
.image-preview {
  width: 200px;
  height: 200px;
  margin-top: var(--margin);
  border-radius: var(--border-radius);
  object-fit: cover;
  overflow: hidden;
  border: var(--border);
}
:focus-visible {
  outline: 1px solid var(--color-bg-accent);
  outline-offset: 2px;
}
.auth-container {
  display: block;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
}
.profile-container {
  max-width: 600px;
  margin: 0 auto;
  padding: var(--padding-xl);
  text-align: center;
}
input.form-control {
  flex: 1;
  padding: var(--padding);
  border: var(--border);
  border-radius: var(--border-radius);
  background: var(--color-bg-secondary);
  margin: var(--margin-s) 0 var(--margin-l) 0;
}
input[type="color"].color-control {
  width: 50px;
  height: 50px;
  border-radius: var(--border-radius);
  border: 1px solid var(--color-bg-primary);
  overflow: hidden;
  cursor: pointer;
  margin: 0 auto;
}
h1,
h2,
h3,
h4,
h5,
h6 {
  text-transform: uppercase;
  letter-spacing: 1px;
}
.description {
  margin-bottom: var(--margin-xxl);
  color: var(--color-text-secondary);
  font-size: var(--font-size);
  line-height: 1.5;
}
.description strong {
  color: var(--color-text-primary);
  font-weight: 500;
}
.ag-panel {
  background: var(--color-bg-primary);
  border-radius: var(--border-radius);
  padding: var(--padding-xl);
  overflow: hidden;
  margin-bottom: var(--margin-xxl);
}
.ag-panel-header {
  padding: var(--padding-xs) var(--padding-xxl);
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius);
}
.ag-panel-header h1,
.ag-panel-header h2 {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}
.ag-panel-header .subtitle,
.subtitle {
  margin: var(--margin-s) 0 0 0;
  font-size: var(--font-size-ms);
  color: var(--color-text-secondary);
}
.ag-panel-header .date-range {
  margin-top: var(--margin);
  display: flex;
  align-items: center;
  gap: var(--margin);
}
.ag-panel-content {
  padding: var(--padding-xxl);
}
.ag-panel-footer {
  padding: var(--padding-xl) var(--padding-xxl);
  border-top: var(--border);
  background: var(--color-bg-primary);
}
.ag-card {
  background: var(--color-bg-primary);
  border: var(--border);
  border-radius: var(--border-radius);
  padding: var(--padding-xl);
  text-align: center;
  transition: box-shadow 0.2s;
}
.ag-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.ag-card-title {
  font-size: var(--font-size);
  color: var(--color-text-secondary);
  margin-bottom: var(--margin);
  font-weight: var(--font-weight-bold);
}
.ag-card-content {
  font-size: var(--font-size-xxl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}
.ag-card-content.large {
  font-size: var(--font-size-xxl);
}
.ag-card-content.small {
  font-size: var(--font-size-l);
}
.ag-card.errors .ag-card-content {
  color: var(--color-danger);
}
.ag-card.warnings .ag-card-content {
  color: var(--color-warning);
}
.ag-card.info .ag-card-content {
  color: var(--color-info);
}
.ag-form-group {
  margin-bottom: var(--margin-xl);
}
.ag-form-group label {
  display: block;
  margin-bottom: var(--margin-s);
  font-size: var(--font-size);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}
.ag-form-group input,
.ag-form-group select,
.ag-form-group textarea {
  width: 100%;
  padding: var(--padding);
  border: var(--border);
  border-radius: var(--border-radius);
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  font-size: var(--font-size);
}
.ag-form-group .form-hint {
  display: block;
  margin-top: var(--margin-s);
  font-size: var(--font-size-ms);
  color: var(--color-text-secondary);
}
.ag-form-group.company-field {
  display: none;
}
.ag-form-group.company-field.visible {
  display: block;
}
.ag-button-group {
  display: flex;
  gap: var(--margin);
  align-items: center;
  justify-content: flex-start;
  padding: var(--padding-l) 0;
}
.ag-button-group.center {
  justify-content: center;
}
.ag-button-group.right {
  justify-content: flex-end;
}
.ag-button-group button {
  flex: 0 1 auto;
}
.ag-table {
  width: 100%;
  overflow-x: auto;
}
.ag-table table {
  width: 100%;
  border-collapse: collapse;
}
.ag-table th,
.ag-table td {
  padding: var(--padding);
  text-align: left;
  border-bottom: var(--border);
}
.ag-table th {
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  font-size: var(--font-size-ms);
}
.ag-badge {
  display: inline-block;
  padding: var(--padding-s) var(--padding);
  border-radius: var(--border-radius);
  font-size: var(--font-size-s);
  font-weight: var(--font-weight-medium);
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
}
.ag-badge.primary {
  background: var(--color-bg-accent);
  color: var(--color-text-inverse);
}
.ag-badge.danger {
  background: var(--color-danger);
  color: var(--color-text-inverse);
}
.ag-badge.success {
  background: var(--color-success);
  color: var(--color-text-inverse);
}
.ag-badge.warning {
  background: var(--color-warning);
  color: var(--color-text-primary);
}
.ag-alert {
  padding: var(--padding-xl);
  border-radius: var(--border-radius);
  margin-bottom: var(--margin-xl);
  border-left: 4px solid var(--color-bg-accent);
  background: var(--color-bg-secondary);
}
.ag-alert.info {
  border-left-color: var(--color-info);
}
.ag-alert.success {
  border-left-color: var(--color-success);
}
.ag-alert.warning {
  border-left-color: var(--color-warning);
}
.ag-alert.danger {
  border-left-color: var(--color-danger);
}
.stats-grid,
.metrics {
  display: grid;
  gap: var(--margin-xl);
  margin-bottom: var(--margin-xxl);
}
.stats-grid {
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}
.metrics {
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}
.color-group {
  display: flex;
  gap: var(--margin-xl);
  justify-content: flex-start;
  margin: var(--margin-xl) 0;
}
.color-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--margin-s);
}
.charts-section {
  display: flex;
  flex-direction: column;
  gap: var(--margin-xxl);
}
.btn-right {
  display: block;
  margin-left: auto;
}
.visible {
  display: block !important;
}
.hidden {
  display: none !important;
}
.dragging {
  opacity: 0.5;
  cursor: move;
}
.drag-over {
  border-top: 2px solid var(--color-bg-accent);
}
.section-description {
  font-size: var(--font-size-ms);
  color: var(--color-text-secondary);
  margin-bottom: var(--margin-xl);
}
.char-count {
  text-align: right;
  font-size: var(--font-size-s);
  color: var(--color-text-secondary);
  margin-top: var(--margin-s);
}
.rate-limit-warning {
  background: var(--color-warning);
  color: var(--color-text-primary);
  padding: var(--padding-xl);
  border-radius: var(--border-radius);
  margin-bottom: var(--margin-xl);
  text-align: center;
  font-weight: var(--font-weight-medium);
}
@media (max-width: 768px) {
  .profile-container {
    padding: var(--padding-xl) var(--padding-l);
  }
  .ag-panel-header,
  .ag-panel-content,
  .ag-panel-footer {
    padding: var(--padding-xl);
  }
  .ag-panel-header h1,
  .ag-panel-header h2 {
    font-size: var(--font-size-l);
  }
  .stats-grid,
  .metrics {
    grid-template-columns: 1fr;
  }
  .color-group {
    flex-wrap: wrap;
  }
  .ag-button-group {
    flex-direction: column;
    align-items: stretch;
  }
  .ag-button-group button {
    width: 100%;
  }
}
